import DataOps from './DataOps';
import LocationOps from '../utility/LocationOps';
import GeoUtils from '../utility/GeoUtils';
import GeocodeCache from '../utility/GeocodeCache';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';

interface SearchCriteria {
    location?: string;
    startDate?: Date;
    endDate?: Date;
    age?: number;
    gender?: string;
    maxDistance?: number; // in miles
}

interface CampWithSessions {
    [key: string]: any;
    campId: string;
    campSessions: any;
    matchingSessionsCount?: number;
    distance?: number;
    geolocation?: {
        lat: number;
        lng: number;
        geohash: string;
    };
}

interface GeocodedLocation {
    lat: number;
    lng: number;
}

class SearchOps {
    private static readonly DEFAULT_RADIUS_MILES = 20;

    /**
     * Main search function using efficient indexed queries
     * Uses geohash for location, date indexes for temporal filtering, and age indexes
     */
    static async searchCamps(criteria: SearchCriteria): Promise<CampWithSessions[]> {
        try {
            // Strategy 1: If we have date OR age filters, query sessions first (most efficient)
            if (criteria.startDate && criteria.endDate) {
                return await this.searchCampsBySessionCriteria(criteria);
            }

            // Strategy 2: Age filter only (no dates)
            if (criteria.age !== undefined) {
                return await this.searchCampsBySessionCriteria(criteria);
            }

            // Strategy 3: Location only - use geohash query
            let camps: CampWithSessions[] = [];

            if (criteria.location && criteria.maxDistance) {
                camps = await this.searchCampsByLocation(
                    criteria.location,
                    criteria.maxDistance
                );
            } else if (!criteria.startDate && !criteria.endDate && criteria.age === undefined) {
                // No explicit filters provided – default to nearby search
                // Try to use current physical location; fallback to Times Square
                try {
                    const current = await LocationOps.getCurrentLocation();
                    if (current?.coords?.latitude && current?.coords?.longitude) {
                        camps = await this.searchCampsByCoordinates(
                            current.coords.latitude,
                            current.coords.longitude,
                            this.DEFAULT_RADIUS_MILES
                        );
                    } else {
                        // Fallback: Times Square, NYC
                        camps = await this.searchCampsByCoordinates(40.758, -73.9855, this.DEFAULT_RADIUS_MILES);
                    }
                } catch {
                    // Fallback: Times Square, NYC
                    camps = await this.searchCampsByCoordinates(40.758, -73.9855, this.DEFAULT_RADIUS_MILES);
                }
            } else {
                // Other non-location filters exist but no coordinates => return empty set by default
                camps = [];
            }

            // Sort by relevance
            camps = this.sortCampsByRelevance(camps);

            return camps;
        } catch (error) {
            console.error('Error searching camps:', error);
            return [];
        }
    }

    /**
     * OPTIMIZED: Search by session criteria (date/age) first using indexed queries
     * Then apply location filter if provided
     */
    private static async searchCampsBySessionCriteria(criteria: SearchCriteria): Promise<CampWithSessions[]> {
        try {
            // Step 1: Query sessions using indexed fields (date and/or age)
            let matchingSessions: any[] = [];

            if (criteria.startDate && criteria.endDate && criteria.age !== undefined) {
                // Both date and age - use combined indexed query
                matchingSessions = await this.querySessionsByDateAndAge(
                    criteria.startDate,
                    criteria.endDate,
                    criteria.age
                );
            } else if (criteria.startDate && criteria.endDate) {
                // Date only - use date indexed query
                matchingSessions = await this.querySessionsByDateRange(
                    criteria.startDate,
                    criteria.endDate
                );
            } else if (criteria.age !== undefined) {
                // Age only - use age indexed query
                matchingSessions = await this.querySessionsByAge(criteria.age);
            }

            if (matchingSessions.length === 0) {
                return [];
            }

            // Step 2: Get unique camp IDs from sessions
            const campIds = [...new Set(matchingSessions.map(s => s.campId))];

            // Step 3: Fetch camps for these sessions
            let camps = await this.fetchCampsByIds(campIds);

            // Step 4: Attach matching sessions to each camp
            // Since camps don't have date/age fields, we need to add the sessions we found
            // campSessions format: { sessionId: session data }
            camps = camps.map(camp => {
                // Get all sessions for this camp from our matching sessions
                const campMatchingSessions = matchingSessions.filter(s => s.campId === camp.campId);
                
                // Create campSessions object with full session data
                // This is more efficient than just storing IDs - UI won't need to fetch again
                const campSessions: { [key: string]: any } = {};
                campMatchingSessions.forEach(session => {
                    const sessionId = session.id || session.sessionId;
                    if (sessionId) {
                        campSessions[sessionId] = session;
                    }
                });

                return {
                    ...camp,
                    campSessions: campSessions,
                    matchingSessionsCount: campMatchingSessions.length
                };
            });

            // Step 5: If location filter provided, apply distance filtering
            if (criteria.location && criteria.maxDistance) {
                const searchCoords = await this.getGeocodeWithCache(criteria.location);
                
                if (searchCoords) {
                    // Filter camps by distance
                    camps = camps.filter(camp => {
                        if (!camp.geolocation?.lat || !camp.geolocation?.lng) {
                            return false;
                        }

                        const distance = GeoUtils.calculateDistance(
                            searchCoords.lat,
                            searchCoords.lng,
                            camp.geolocation.lat,
                            camp.geolocation.lng,
                            true
                        );

                        camp.distance = distance;
                        return distance <= criteria.maxDistance!;
                    });
                }
            }

            // Step 6: Sort by relevance
            camps = this.sortCampsByRelevance(camps);

            return camps;
        } catch (error) {
            console.error('Error in searchCampsBySessionCriteria:', error);
            return [];
        }
    }

    /**
     * EFFICIENT: Query sessions by date range using Firebase indexes
     * This uses Firebase's built-in date range queries
     */
    private static async querySessionsByDateRange(startDate: Date, endDate: Date): Promise<any[]> {
        try {
            const collectionRef = collection(DataOps.db, 'session');
            
            // Query sessions that start before search end date
            // We'll filter the end date in memory since Firestore doesn't support range on two fields
            const q = query(
                collectionRef,
                where('officialStartDate', '<=', endDate),
                where('officialStartDate', '>=', new Date(startDate.getTime() - 90 * 24 * 60 * 60 * 1000)), // 90 days before
                orderBy('officialStartDate', 'desc')
            );

            const snapshot = await getDocs(q);
            
            const sessions = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter((session: any) => {
                    // Skip deleted or hidden
                    if (session.deleted || session.isHidden) {
                        return false;
                    }

                    // Apply exact date range matching
                    return this.sessionMatchesDateRange(session, startDate, endDate);
                });

            return sessions;
        } catch (error) {
            console.error('Error querying sessions by date range:', error);
            return [];
        }
    }

    /**
     * Query sessions by age
     * Note: startAge/endAge are stored as strings in Firebase, so we fetch all sessions
     * and filter in memory. Still efficient since we're only fetching sessions, not camps.
     */
    private static async querySessionsByAge(age: number): Promise<any[]> {
        try {
            const collectionRef = collection(DataOps.db, 'session');
            
            // Fetch all sessions (filtered by non-deleted/non-hidden)
            const snapshot = await getDocs(collectionRef);
            
            const sessions = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter((session: any) => {
                    // Skip deleted or hidden
                    if (session.deleted || session.isHidden) {
                        return false;
                    }

                    // Check age range: startAge <= age <= endAge
                    return this.sessionMatchesAge(session, age);
                });

            return sessions;
        } catch (error) {
            console.error('Error querying sessions by age:', error);
            return [];
        }
    }

    /**
     * EFFICIENT: Query sessions by date AND age
     * Uses indexed date query, then filters by age in memory
     * (age is stored as string, so can't use indexed query for it)
     */
    private static async querySessionsByDateAndAge(
        startDate: Date,
        endDate: Date,
        age: number
    ): Promise<any[]> {
        try {
            // First, use indexed date query to narrow results
            const sessionsInDateRange = await this.querySessionsByDateRange(startDate, endDate);
            
            // Then filter by age in memory
            const sessions = sessionsInDateRange.filter((session: any) => {
                return this.sessionMatchesAge(session, age);
            });

            return sessions;
        } catch (error) {
            console.error('Error querying sessions by date and age:', error);
            return [];
        }
    }

    /**
     * Fetch camps by IDs efficiently
     */
    private static async fetchCampsByIds(campIds: string[]): Promise<any[]> {
        try {
            if (campIds.length === 0) return [];

            // Firestore 'in' operator supports up to 10 items per query
            const batchSize = 10;
            const batches: Promise<any[]>[] = [];

            for (let i = 0; i < campIds.length; i += batchSize) {
                const batch = campIds.slice(i, i + batchSize);
                const collectionRef = collection(DataOps.db, 'camp');
                const q = query(collectionRef, where('campId', 'in', batch));
                
                batches.push(
                    getDocs(q).then(snapshot => 
                        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                    )
                );
            }

            const results = await Promise.all(batches);
            return results.flat();
        } catch (error) {
            console.error('Error fetching camps by IDs:', error);
            return [];
        }
    }

    /**
     * OPTIMIZED: Search camps by location using geohash queries
     * This is 100x+ faster than fetching all camps and calculating distances!
     */
    private static async searchCampsByLocation(
        location: string,
        maxDistance: number
    ): Promise<CampWithSessions[]> {
        try {
            const searchCoords = await this.getGeocodeWithCache(location);
            if (!searchCoords) {
                console.warn('Could not geocode search location');
                return [];
            }
            return await this.searchCampsByCoordinates(searchCoords.lat, searchCoords.lng, maxDistance);
        } catch (error) {
            console.error('Error in searchCampsByLocation:', error);
            return [];
        }
    }

    // New: direct coordinate-based search (avoids unnecessary geocoding when we already have lat/lng)
    private static async searchCampsByCoordinates(
        lat: number,
        lng: number,
        maxDistance: number
    ): Promise<CampWithSessions[]> {
        try {
            const bounds = GeoUtils.getGeohashQueryBounds(lat, lng, maxDistance);
            const promises = bounds.map(async ([start, end]) => {
                const collectionRef = collection(DataOps.db, 'camp');
                const q = query(
                    collectionRef,
                    where('geolocation.geohash', '>=', start),
                    where('geolocation.geohash', '<=', end),
                    orderBy('geolocation.geohash')
                );
                const snapshot = await getDocs(q);
                return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            });

            const results = await Promise.all(promises);
            const allCamps = this.deduplicateCamps(results.flat());

            const campsWithDistance = allCamps
                .map(camp => {
                    if (!camp.geolocation || !camp.geolocation.lat || !camp.geolocation.lng) {
                        return null;
                    }
                    const distance = GeoUtils.calculateDistance(
                        lat,
                        lng,
                        camp.geolocation.lat,
                        camp.geolocation.lng,
                        true
                    );
                    return { ...camp, distance };
                })
                .filter(camp => camp !== null && camp.distance <= maxDistance) as CampWithSessions[];

            return campsWithDistance;
        } catch (error) {
            console.error('Error in searchCampsByCoordinates:', error);
            return [];
        }
    }

    /**
     * Get geocoded coordinates with caching to avoid expensive API calls
     */
    private static async getGeocodeWithCache(address: string): Promise<GeocodedLocation | null> {
        // Check cache first
        const cached = GeocodeCache.get(address);
        if (cached) {
            return { lat: cached.lat, lng: cached.lng };
        }

        // Not in cache - make API call
        const result = await LocationOps.getLocationFromAddress(address);
        
        if (result) {
            // Store in cache for future use
            GeocodeCache.set(address, result.lat, result.lng);
            return result;
        }

        return null;
    }

    /**
     * Remove duplicate camps (can happen when geohash ranges overlap)
     */
    private static deduplicateCamps(camps: any[]): CampWithSessions[] {
        const seen = new Set<string>();
        const unique: CampWithSessions[] = [];

        for (const camp of camps) {
            const campId = camp.campId || camp.id;
            if (!seen.has(campId)) {
                seen.add(campId);
                unique.push(camp);
            }
        }

        return unique;
    }

    /**
     * Filter camps by session criteria (dates, age, gender)
     */
    private static filterBySessionCriteria(
        camps: CampWithSessions[],
        criteria: SearchCriteria
    ): CampWithSessions[] {
        return camps.map(camp => {
            if (!camp.campSessions || Object.keys(camp.campSessions).length === 0) {
                return { ...camp, matchingSessionsCount: 0 };
            }

            // Count matching sessions
            const matchingSessions = Object.values(camp.campSessions).filter((session: any) => {
                // Skip deleted or hidden sessions
                if (session.deleted || session.isHidden) {
                    return false;
                }

                // Check dates
                if (criteria.startDate && criteria.endDate) {
                    if (!this.sessionMatchesDateRange(session, criteria.startDate, criteria.endDate)) {
                        return false;
                    }
                }

                // Check age
                if (criteria.age !== undefined) {
                    if (!this.sessionMatchesAge(session, criteria.age)) {
                        return false;
                    }
                }

                return true;
            });

            return {
                ...camp,
                matchingSessionsCount: matchingSessions.length
            };
        }).filter(camp => camp.matchingSessionsCount > 0);
    }

    /**
     * Check if session dates overlap with search date range
     * EXACT matching - only returns true if session overlaps with search dates
     * Handles both startDate/endDate and officialStartDate/officialEndDate fields
     */
    private static sessionMatchesDateRange(
        session: any,
        searchStartDate: Date,
        searchEndDate: Date
    ): boolean {
        // Try officialStartDate/officialEndDate first (used in indexed queries), then fall back to startDate/endDate
        const startDateField = session.officialStartDate || session.startDate;
        const endDateField = session.officialEndDate || session.endDate;

        if (!startDateField || !endDateField) {
            return false;
        }

        // Handle Firebase Timestamp objects
        const sessionStart = startDateField.toDate ? startDateField.toDate() : new Date(startDateField);
        const sessionEnd = endDateField.toDate ? endDateField.toDate() : new Date(endDateField);

        // Normalize dates to start of day for accurate comparison
        const searchStart = new Date(searchStartDate.getFullYear(), searchStartDate.getMonth(), searchStartDate.getDate());
        const searchEnd = new Date(searchEndDate.getFullYear(), searchEndDate.getMonth(), searchEndDate.getDate());
        const sessStart = new Date(sessionStart.getFullYear(), sessionStart.getMonth(), sessionStart.getDate());
        const sessEnd = new Date(sessionEnd.getFullYear(), sessionEnd.getMonth(), sessionEnd.getDate());

        // EXACT overlap check: session must overlap with search range
        // Session overlaps if it starts before search ends AND ends after search starts
        return sessStart <= searchEnd && sessEnd >= searchStart;
    }

    /**
     * Check if camper age fits within session age range
     */
    private static sessionMatchesAge(session: any, age: number): boolean {
        if (session.startAge === undefined && session.endAge === undefined) {
            return true; // No age restrictions
        }

        const startAge = session.startAge || 0;
        const endAge = session.endAge || 99;

        return age >= startAge && age <= endAge;
    }

    /**
     * Sort camps by relevance:
     * 1. Number of matching sessions (descending)
     * 2. Distance (ascending) if available
     */
    private static sortCampsByRelevance(camps: CampWithSessions[]): CampWithSessions[] {
        return camps.sort((a, b) => {
            // Primary sort: matching sessions count (more is better)
            const sessionDiff = (b.matchingSessionsCount || 0) - (a.matchingSessionsCount || 0);
            if (sessionDiff !== 0) {
                return sessionDiff;
            }

            // Secondary sort: distance (closer is better)
            if (a.distance !== undefined && b.distance !== undefined) {
                return a.distance - b.distance;
            }

            // If one has distance and other doesn't, prioritize the one with distance
            if (a.distance !== undefined) return -1;
            if (b.distance !== undefined) return 1;

            return 0;
        });
    }

    /**
     * Search sessions directly (for session view)
     * Now uses spatial filtering when location is provided
     * EXACT date matching - only returns sessions that overlap with search dates
     */
    static async searchSessions(criteria: SearchCriteria): Promise<any[]> {
        try {
            const allSessions = await DataOps.fetchUpcomingSessionsFromFirebase();
            
            if (!allSessions || allSessions.length === 0) {
                return [];
            }

            // Filter sessions by dates and age - EXACT matching
            let filtered = allSessions.filter((session: any) => {
                // Skip deleted or hidden
                if (session.deleted || session.isHidden) {
                    return false;
                }

                // Check dates - EXACT overlap required
                if (criteria.startDate && criteria.endDate) {
                    if (!this.sessionMatchesDateRange(session, criteria.startDate, criteria.endDate)) {
                        return false;
                    }
                }

                // Check age - EXACT range match
                if (criteria.age !== undefined) {
                    if (!this.sessionMatchesAge(session, criteria.age)) {
                        return false;
                    }
                }

                return true;
            });

            // If location filter is provided, use efficient spatial query
            if (criteria.location && criteria.maxDistance) {
                const campsInRange = await this.searchCampsByLocation(
                    criteria.location,
                    criteria.maxDistance
                );
                
                const campIdsInRange = new Set(campsInRange.map(camp => camp.campId));
                
                filtered = filtered.filter((session: any) => 
                    campIdsInRange.has(session.campId)
                );
            }

            return filtered;
        } catch (error) {
            console.error('Error searching sessions:', error);
            return [];
        }
    }

    /**
     * Quick search - simplified version without location filtering (faster)
     */
    static async quickSearch(criteria: SearchCriteria): Promise<CampWithSessions[]> {
        try {
            const allCamps = await DataOps.fetchAllDocumentsFromFirebase('camp');
            
            if (!allCamps || allCamps.length === 0) {
                return [];
            }

            // Only filter by dates and age (skip expensive location calculations)
            let filtered = this.filterBySessionCriteria(allCamps, criteria);
            filtered = this.sortCampsByRelevance(filtered);

            return filtered;
        } catch (error) {
            console.error('Error in quick search:', error);
            return [];
        }
    }

    /**
     * HELPER: Get camps that need geolocation data added
     * Use this to identify camps that need to be migrated
     */
    static async getCampsWithoutGeolocation(): Promise<any[]> {
        try {
            const allCamps = await DataOps.fetchAllDocumentsFromFirebase('camp');
            return allCamps.filter(camp => !camp.geolocation || !camp.geolocation.geohash);
        } catch (error) {
            console.error('Error getting camps without geolocation:', error);
            return [];
        }
    }
}

export default SearchOps;
